/**
 * v-reveal —— 元素进入视口时淡入上浮
 *
 * 用法：
 *   <section v-reveal>...</section>
 *   <div v-reveal="120">...</div>   // 120ms 延迟
 *
 * 降级策略：
 *   - 浏览器不支持 IntersectionObserver：直接显示
 *   - 用户开启「减少动态效果」：直接显示
 *   - 快速滚动导致 IO 漏判：滚动时兜底检查，避免内容永久隐藏
 */
const supportsObserver = typeof window !== 'undefined' && 'IntersectionObserver' in window

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const pending = new Set()
let bound = false

function reveal(el) {
  el.classList.add('is-visible')
  pending.delete(el)
  if (el.__revealObserver) {
    el.__revealObserver.disconnect()
    el.__revealObserver = null
  }
}

function checkPending() {
  if (!pending.size) return
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight
  pending.forEach((el) => {
    if (!el.isConnected) {
      pending.delete(el)
      return
    }
    // 顶部已经滑出视口的元素 top 为负值，同样需要显示
    if (el.getBoundingClientRect().top < viewportHeight + 40) {
      reveal(el)
    }
  })
}

function bindScrollFallback() {
  if (bound) return
  bound = true
  window.addEventListener('scroll', checkPending, { passive: true })
  window.addEventListener('resize', checkPending)
}

export default {
  inserted(el, binding) {
    el.classList.add('reveal')

    if (binding.value) {
      el.style.setProperty('--reveal-delay', `${binding.value}ms`)
    }

    if (!supportsObserver || prefersReducedMotion()) {
      el.classList.add('is-visible')
      return
    }

    pending.add(el)
    bindScrollFallback()

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) reveal(entry.target)
        })
      },
      { rootMargin: '0px 0px -36px 0px', threshold: 0.04 }
    )

    observer.observe(el)
    el.__revealObserver = observer
  },
  unbind(el) {
    pending.delete(el)
    if (el.__revealObserver) {
      el.__revealObserver.disconnect()
      el.__revealObserver = null
    }
  }
}
